import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { setAccessToken } from '../api/client';
import { queryClient } from '../api/queryClient';
import { authService } from '../services/authService';
import {
  ensureWebPushRegistered,
  finishLoginPushPermission,
  markPushPromptPendingAfterLogin,
  clearPushPromptPendingAfterLogin,
  disableWebPushOnLogout,
} from '../push/webPush';
import { isStaff } from '../utils/roles';
import {
  clearAuthSession,
  getStoredToken,
  getStoredUser,
  saveAuthSession,
} from './authStorage';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [capabilities, setCapabilities] = useState(null);
  const [unreadAlertCount, setUnreadAlertCount] = useState(0);
  const [hydrating, setHydrating] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const applyViewer = useCallback((viewer) => {
    if (!viewer || typeof viewer !== 'object') return;
    if (viewer.user?.id && viewer.user?.role) {
      setUser(viewer.user);
      const token = getStoredToken();
      if (token) saveAuthSession(token, viewer.user);
    }
    if (viewer.capabilities) setCapabilities(viewer.capabilities);
    if (typeof viewer.unreadAlertCount === 'number') {
      setUnreadAlertCount(viewer.unreadAlertCount);
    }
  }, []);

  useEffect(() => {
    const token = getStoredToken();
    const storedUser = getStoredUser();
    if (token && storedUser) {
      setAccessToken(token);
      setUser(storedUser);
    } else {
      clearAuthSession();
      setAccessToken(null);
      setUser(null);
    }
    setHydrating(false);
  }, []);

  const applyAuthResult = useCallback(
    async (res, { permissionWork } = {}) => {
      if (!res?.accessToken || !res?.user?.id || !res?.user?.role) {
        throw new Error('Sign-in response was incomplete. Please try again.');
      }
      setAccessToken(res.accessToken);
      saveAuthSession(res.accessToken, res.user);
      queryClient.clear();
      setUser(res.user);
      if (res.viewer) {
        applyViewer(res.viewer);
      } else {
        setCapabilities(null);
        setUnreadAlertCount(0);
      }
      void ensureWebPushRegistered();
      if (permissionWork) {
        const result = await finishLoginPushPermission(permissionWork);
        if (result === 'default') {
          markPushPromptPendingAfterLogin();
        }
      } else {
        markPushPromptPendingAfterLogin();
      }
      return res.user;
    },
    [applyViewer],
  );

  const login = useCallback(
    async (loginId, password, options) => {
      setError(null);
      setSubmitting(true);
      try {
        const res = await authService.login(loginId, password);
        return await applyAuthResult(res, options);
      } catch (e) {
        const message = e instanceof Error ? e.message : 'Login failed';
        setError(message);
        throw e;
      } finally {
        setSubmitting(false);
      }
    },
    [applyAuthResult],
  );

  const signup = useCallback(
    async (details, options) => {
      setError(null);
      setSubmitting(true);
      try {
        const res = await authService.signup(details);
        if (res?.user?.role && res.user.role !== 'reader') {
          throw new Error('Signup created an unexpected account type.');
        }
        return await applyAuthResult(res, options);
      } catch (e) {
        const message = e instanceof Error ? e.message : 'Sign up failed';
        setError(message);
        throw e;
      } finally {
        setSubmitting(false);
      }
    },
    [applyAuthResult],
  );

  const logout = useCallback(async () => {
    try {
      await disableWebPushOnLogout();
    } catch {
      /* still sign out locally */
    }
    setAccessToken(null);
    clearAuthSession();
    setUser(null);
    setCapabilities(null);
    setUnreadAlertCount(0);
    setError(null);
    clearPushPromptPendingAfterLogin();
    queryClient.clear();
  }, []);

  const value = useMemo(
    () => ({
      user,
      capabilities,
      unreadAlertCount,
      applyViewer,
      hydrating,
      submitting,
      loading: hydrating || submitting,
      error,
      login,
      signup,
      logout,
      isStaff: user ? isStaff(user.role) : false,
    }),
    [
      user,
      capabilities,
      unreadAlertCount,
      applyViewer,
      hydrating,
      submitting,
      error,
      login,
      signup,
      logout,
    ],
  );

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
